/**
 * v3.18-1321 — Employee Duplicate Resolution dialog (six actions).
 *
 * Opens BEFORE an employee is created if the entered email already exists.
 * The operator picks from the six approved actions:
 *   • Use Existing Employee
 *   • Add Employee to Another Company        (hidden if already a member)
 *   • Move Employee to Another Company       (requires confirmation + reason)
 *   • Merge Into Existing Employee           (folds create-form fields in
 *                                             non-destructively)
 *   • Create Intentional Duplicate           (Admin only, mandatory reason)
 *   • Cancel
 *
 * We never expose internal IDs, UUIDs, or debug tokens to the operator.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter }
  from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Loader2, AlertTriangle, User, Building2, GitMerge, ArrowRightLeft,
         Plus, Ban, Eye, ShieldAlert } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const ICONS = {
  use_existing:     Eye,
  add_company:      Plus,
  move_company:     ArrowRightLeft,
  merge:            GitMerge,
  create_duplicate: ShieldAlert,
  cancel:           Ban,
};

/**
 * Props:
 *   open, onOpenChange
 *   email                          — the entered email
 *   targetCompanyId, targetCompanyName
 *   incoming                       — { full_name, phone, extension, position, ... }
 *   onUseExisting(employee)        — open existing employee for edit
 *   onDone()                       — refresh HR list & close create dialog
 */
export function EmployeeDuplicateDialog({
  open, onOpenChange, email, targetCompanyId, targetCompanyName, incoming = {},
  onUseExisting, onDone,
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData]       = useState(null);
  const [selected, setSel]    = useState(null);
  const [reason, setReason]   = useState('');
  const [reasonErr, setRErr]  = useState('');
  const [moveConfirmed, setMC]= useState(false);
  const [busy, setBusy]       = useState(false);

  useEffect(() => {
    if (!open || !email) return;
    let cancelled = false;
    setSel(null); setReason(''); setRErr(''); setMC(false);
    (async () => {
      setLoading(true);
      try {
        const params = { email };
        if (targetCompanyId) params.target_company_id = targetCompanyId;
        const r = await api.get('/employees/duplicate-check', { params });
        if (!cancelled) setData(r.data || null);
      } catch (e) {
        toast.error(String(e?.response?.data?.detail
                           || 'Could not check for existing employee.'));
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, email, targetCompanyId]);

  const employee = data?.employee;
  // The internal handle we send to backend endpoints.  Never displayed.
  const employeeHandle = employee?._id_for_actions;
  const actions  = data?.actions_available || [];

  const needsReason  = selected === 'move_company'
                    || selected === 'create_duplicate'
                    || selected === 'merge';
  const needsConfirm = selected === 'move_company';

  const invoke = async () => {
    if (!selected || !employeeHandle) return;
    if (needsReason && !reason.trim()) {
      setRErr('A reason is required for this action.'); return;
    }
    if (needsConfirm && !moveConfirmed) {
      setRErr('Please confirm this destructive action.'); return;
    }
    setRErr(''); setBusy(true);
    try {
      switch (selected) {
        case 'use_existing':
          onUseExisting && onUseExisting(employee);
          onOpenChange(false);
          break;

        case 'add_company':
          await api.post(`/employees/${employeeHandle}/memberships`, {
            company_id: targetCompanyId,
            reason: 'Added via duplicate resolution',
          });
          toast.success(`${employee.full_name} added to ${targetCompanyName || 'target company'}.`);
          onDone && onDone(); onOpenChange(false);
          break;

        case 'move_company':
          await api.post(`/employees/${employeeHandle}/move-company`, {
            company_id: targetCompanyId,
            reason: reason.trim(),
            confirmed: true,
          });
          toast.success(`${employee.full_name} moved to ${targetCompanyName || 'target company'}.`);
          onDone && onDone(); onOpenChange(false);
          break;

        case 'merge':
          await api.post(`/employees/${employeeHandle}/merge`, {
            target_company_id: targetCompanyId,
            incoming,
            reason: reason.trim(),
          });
          toast.success(`Merged into ${employee.full_name}.`);
          onUseExisting && onUseExisting(employee);
          onDone && onDone(); onOpenChange(false);
          break;

        case 'create_duplicate':
          // Bubble up so the parent can call POST /users with
          // force_create_duplicate=true and the reason.
          onDone && onDone({ force_create_duplicate: true,
                             duplicate_reason: reason.trim() });
          onOpenChange(false);
          break;

        case 'cancel':
        default:
          onOpenChange(false);
      }
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || 'Action failed.'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg"
                     data-testid="employee-duplicate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-800">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            Employee Already Exists
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking…
          </div>
        )}

        {!loading && employee && (
          <>
            <div className="rounded-lg border bg-slate-50 p-3 space-y-1 text-sm"
                 data-testid="employee-duplicate-summary">
              <div className="flex items-center gap-2">
                <User className="h-3.5 w-3.5 text-slate-500" />
                <span className="font-semibold text-slate-900">{employee.full_name}</span>
              </div>
              <div className="text-xs text-slate-500 pl-5">{employee.email}</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 pl-5 text-xs">
                <div>
                  <span className="uppercase tracking-wider text-slate-400 mr-1">Current Company</span>
                  <div className="text-slate-700 font-medium flex items-center gap-1">
                    <Building2 className="h-3 w-3 text-slate-400" />
                    {employee.current_company}
                  </div>
                </div>
                <div>
                  <span className="uppercase tracking-wider text-slate-400 mr-1">Status</span>
                  <div className="text-slate-700 font-medium">{employee.status}</div>
                </div>
                <div className="col-span-2">
                  <span className="uppercase tracking-wider text-slate-400 mr-1">Role</span>
                  <div className="text-slate-700 font-medium">{employee.role}</div>
                </div>
                {targetCompanyName && (
                  <div className="col-span-2 pt-1 border-t border-slate-200 mt-1">
                    <span className="uppercase tracking-wider text-slate-400 mr-1">You Are Attempting To Add In</span>
                    <div className="text-slate-700 font-medium">{targetCompanyName}
                      {data?.already_member && (
                        <span className="ml-2 text-[10px] text-emerald-700 uppercase tracking-wider">already a member</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-2 space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pt-1">
                Available Actions
              </div>
              {actions.map(a => {
                const Ico = ICONS[a.key] || User;
                return (
                  <label key={a.key}
                         className={`flex items-center gap-2 rounded border px-3 py-2 cursor-pointer transition-colors
                                     ${selected === a.key
                                       ? 'border-indigo-400 bg-indigo-50'
                                       : 'border-slate-200 hover:bg-slate-50'}`}
                         data-testid={`employee-duplicate-action-${a.key}`}>
                    <input
                      type="radio"
                      name="dup-action"
                      checked={selected === a.key}
                      onChange={() => { setSel(a.key); setRErr(''); }}
                      className="text-indigo-600"
                    />
                    <Ico className={`h-3.5 w-3.5 ${
                      a.key === 'create_duplicate' ? 'text-amber-700' :
                      a.key === 'move_company'     ? 'text-orange-600' :
                      a.key === 'merge'            ? 'text-indigo-600' :
                      a.key === 'add_company'      ? 'text-emerald-600' :
                      a.key === 'cancel'           ? 'text-slate-400' :
                      'text-slate-500'
                    }`}/>
                    <span className={`text-sm ${
                      a.key === 'create_duplicate' ? 'text-amber-700 font-semibold' :
                      a.key === 'cancel'           ? 'text-slate-500' :
                      'text-slate-800'
                    }`}>{a.label}</span>
                  </label>
                );
              })}
            </div>

            {(needsReason || needsConfirm) && selected !== 'cancel' && selected != null && (
              <div className={`rounded border p-3 space-y-2 mt-2 ${
                selected === 'create_duplicate' || selected === 'move_company'
                  ? 'border-amber-200 bg-amber-50'
                  : 'border-indigo-200 bg-indigo-50'
              }`} data-testid="employee-duplicate-reason-panel">
                <div className="text-xs font-semibold text-slate-700">
                  {selected === 'create_duplicate' && 'Intentional duplicates require a written reason (immutable audit).'}
                  {selected === 'move_company'     && 'Moving an employee is destructive. Confirm and provide a reason.'}
                  {selected === 'merge'            && 'Explain why you are merging (immutable audit).'}
                </div>
                <Textarea
                  rows={2}
                  value={reason}
                  onChange={e => { setReason(e.target.value); setRErr(''); }}
                  placeholder="Reason (required)"
                  data-testid="employee-duplicate-reason"
                />
                {needsConfirm && (
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input type="checkbox" checked={moveConfirmed}
                           onChange={e => setMC(e.target.checked)}
                           data-testid="employee-duplicate-move-confirm"/>
                    I understand this reassigns the employee&apos;s primary company.
                  </label>
                )}
                {reasonErr && (
                  <div className="text-xs text-red-600">{reasonErr}</div>
                )}
              </div>
            )}
          </>
        )}

        {!loading && !employee && data && (
          <div className="py-8 text-center text-sm text-slate-500">
            No existing employee found. Safe to create.
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}
                  disabled={busy}
                  data-testid="employee-duplicate-cancel">
            Cancel
          </Button>
          <Button
            onClick={invoke}
            disabled={!selected || selected === 'cancel' || busy}
            data-testid="employee-duplicate-continue">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EmployeeDuplicateDialog;
